<template>
  <div ref="containerRef" class="cb-data-charts-raw" :class="{ 'is-fullscreen': isFullscreen }" :style="containerStyle">
    <!-- View Controls -->
    <div v-if="showViewControls" class="cb-data-charts-raw__controls">
      <!-- Chart Type Switcher -->
      <NDropdown
        v-if="props.data?.view_controls?.enable_chart_type_switcher"
        :options="chartTypeSwitcherOptions"
        trigger="click"
        @select="handleChartTypeChange"
      >
        <CButton quaternary size="small">
          <template #icon>
            <CIcon icon="chart-bar" />
          </template>
        </CButton>
      </NDropdown>

      <!-- Fullscreen Button -->
      <CButton
        v-if="props.data?.view_controls?.enable_fullscreen_btn"
        quaternary
        size="small"
        @click="toggleFullscreen"
      >
        <template #icon>
          <CIcon :icon="isFullscreen ? 'compress' : 'expand'" />
        </template>
      </CButton>
    </div>

    <canvas ref="chartRef" class="w-full h-full"></canvas>
  </div>
</template>

<script setup lang="ts">
import type { ExtendedChartConfiguration } from '@canvas-builder/components/ui/DataCharts/types'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCrmShape from '@canvas-builder/composables/useCrmShape'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import { loadScript } from '@lib/util/dom'
import { renderTemplate } from '@lib/util/handlebars'
import type { ChartConfiguration } from 'chart.js'
import { merge, isEmpty } from 'lodash-es'
import type { ComputedRef } from 'vue'
import { computed, inject, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import type { CanvasTheme } from '@canvas-builder/types/canvas'
import { NDropdown } from 'naive-ui'
import { useI18n } from 'vue-i18n'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'

// Declare Chart.js on window
declare global {
  interface Window {
    Chart: any
    _chartJsPromise?: Promise<void>
  }
}

const { t } = useI18n()
const chartRef = ref<HTMLCanvasElement | null>(null)
const containerRef = ref<HTMLElement | null>(null)
let chart: InstanceType<typeof window.Chart> | null = null
const isChartLoaded = ref(false)
const isFullscreen = ref(false)
const currentChartType = ref<string>('')

const props = defineProps<{
  data?: ExtendedChartConfiguration & {
    context?: any
    _original_chart_type?: string
  }
  isPreview?: boolean
  isEditMode?: boolean
}>()

const showViewControls = computed(() => {
  if (props.isPreview || props.isEditMode) return false
  return props.data?.view_controls?.enable_fullscreen_btn || props.data?.view_controls?.enable_chart_type_switcher
})

const containerStyle = computed(() => {
  if (isFullscreen.value || props.isPreview) {
    return {}
  }
  return {
    height: props.data?.chart_height ? `${props.data.chart_height}px` : undefined,
    width: props.data?.chart_width ? `${props.data.chart_width}%` : undefined,
    minHeight: props.data?.chart_height ? `${props.data.chart_height}px` : undefined,
    maxHeight: props.data?.chart_height ? `${props.data.chart_height}px` : undefined,
  }
})

// Determine chart data structure based on original type
const originalChartType = computed(() => {
  return props.data?.type || props.data?._primary_chart_type || 'bar'
})

const isScatterChart = computed(() => originalChartType.value === 'scatter')
const isBubbleChart = computed(() => originalChartType.value === 'bubble')

// 1D chart types (label/value based)
const labelValueChartTypes = [
  { label: t('canvasUI.canvasBuilder.dataCharts.types.bar'), key: 'bar' },
  { label: t('canvasUI.canvasBuilder.dataCharts.types.line'), key: 'line' },
  { label: t('canvasUI.canvasBuilder.dataCharts.types.area'), key: 'area' },
  { label: t('canvasUI.canvasBuilder.dataCharts.types.pie'), key: 'pie' },
  { label: t('canvasUI.canvasBuilder.dataCharts.types.doughnut'), key: 'doughnut' },
  { label: t('canvasUI.canvasBuilder.dataCharts.types.polarArea'), key: 'polarArea' },
  { label: t('canvasUI.canvasBuilder.dataCharts.types.radar'), key: 'radar' },
]

// 2D chart types (x/y coordinate based) - scatter only
const scatterChartTypes = [{ label: t('canvasUI.canvasBuilder.dataCharts.types.scatter'), key: 'scatter' }]

// 3D chart types (x/y/r coordinate based) - bubble only, no switching allowed
const bubbleChartTypes = [{ label: t('canvasUI.canvasBuilder.dataCharts.types.bubble'), key: 'bubble' }]

// Only show compatible chart types based on the original data structure
// Bubble charts can't switch types because they require x/y/r data
// Scatter charts can only stay as scatter (x/y data)
// Label/value charts can switch among themselves
const chartTypeSwitcherOptions = computed(() => {
  if (isBubbleChart.value) return bubbleChartTypes
  if (isScatterChart.value) return scatterChartTypes
  return labelValueChartTypes
})

function toggleFullscreen() {
  if (!containerRef.value) return

  if (!isFullscreen.value) {
    isFullscreen.value = true
  } else {
    isFullscreen.value = false
  }

  // Resize chart after fullscreen toggle
  nextTick(() => {
    if (chart) {
      chart.resize()
    }
  })
}

function handleChartTypeChange(newType: string) {
  if (!chart || !props.data) return

  currentChartType.value = newType

  // Reinitialize chart with new type
  initChart(newType)
}

// Handle escape key to exit fullscreen
function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && isFullscreen.value) {
    isFullscreen.value = false
    nextTick(() => {
      if (chart) {
        chart.resize()
      }
    })
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})

const sectionListSectionInfo = inject<
  ComputedRef<{
    usedInSectionId: string
    sectionIdx: number
    sectionListId: string
  } | null>
>(
  'sectionListSectionInfo',
  computed(() => null),
)

const { activeCanvas, activeCanvasSections, isCanvas } = useCanvas()
const { crmShape } = useCrmShape()
const { activeTheme, maintainSectionTheme } = useCanvasTheme()

const usedInSectionId = computed(() => {
  return sectionListSectionInfo.value?.usedInSectionId
})

const sectionTheme = computed(() => {
  return usedInSectionId.value ? (activeCanvasSections.value[usedInSectionId.value]?.theme as CanvasTheme) : undefined
})

const palette = computed(() => {
  return (
    (maintainSectionTheme.value && !sectionTheme.value?.inherit_theme_from_parent && sectionTheme.value?.palette) ||
    activeTheme.value?.settings?.palette ||
    []
  )
})

// Ensure Chart.js is loaded only once across all instances
function ensureChartJsLoaded() {
  if (!window._chartJsPromise) {
    window._chartJsPromise = new Promise((resolve) => {
      if (window.Chart) {
        resolve()
      } else {
        loadScript('https://cdn.jsdelivr.net/npm/chart.js@4.4.7/dist/chart.umd.min.js', () => {
          resolve()
        })
      }
    })
  }
  return window._chartJsPromise
}

// Normalize R values for bubble charts to a reasonable display range
// Returns objects with both normalized r (for display) and originalR (for tooltips)
function normalizeRValues(
  values: Array<{ x: number; y: number; r: number }>,
  minRadius: number = 5,
  maxRadius: number = 40,
): Array<{ x: number; y: number; r: number; originalR: number }> {
  if (values.length === 0) return []

  const rValues = values.map((v) => v.r)
  const minR = Math.min(...rValues)
  const maxR = Math.max(...rValues)

  // If all values are the same, use a middle radius
  if (minR === maxR) {
    return values.map((v) => ({
      ...v,
      r: (minRadius + maxRadius) / 2,
      originalR: v.r,
    }))
  }

  // Normalize to the range [minRadius, maxRadius], keeping original value
  return values.map((v) => ({
    ...v,
    r: minRadius + ((v.r - minR) / (maxR - minR)) * (maxRadius - minRadius),
    originalR: v.r,
  }))
}

function processSeriesDataPoints(
  dataPoints: any[],
  chartType: string,
  context: any,
): { labels: string[]; values: (number | { x: number; y: number; r?: number; originalR?: number })[] } {
  const isScatterChart = chartType === 'scatter'
  const isBubbleChart = chartType === 'bubble'
  const isXYChart = isScatterChart || isBubbleChart
  const processedData = {
    labels: [] as string[],
    values: [] as (number | { x: number; y: number; r?: number; originalR?: number })[],
  }

  // For bubble charts, collect raw data first then normalize
  const rawBubbleData: Array<{ label: string; x: number; y: number; r: number }> = []

  dataPoints?.forEach((point) => {
    let label = point.label

    try {
      if (typeof label === 'string') {
        label = renderTemplate(label, context)
      }

      if (isXYChart) {
        let xValue = point.x_value ?? '0'
        let yValue = point.y_value ?? '0'
        let rValue = point.r_value ?? '10' // Default radius of 10 for bubble charts

        if (typeof xValue === 'string') {
          xValue = renderTemplate(xValue, context)
        }
        if (typeof yValue === 'string') {
          yValue = renderTemplate(yValue, context)
        }
        if (typeof rValue === 'string') {
          rValue = renderTemplate(rValue, context)
        }

        if (
          typeof label === 'string' &&
          label.includes(',') &&
          typeof xValue === 'string' &&
          xValue.includes(',') &&
          typeof yValue === 'string' &&
          yValue.includes(',')
        ) {
          const labels = label.split(',')
          const xValues = xValue.split(',')
          const yValues = yValue.split(',')
          const rValues = typeof rValue === 'string' && rValue.includes(',') ? rValue.split(',') : []

          labels.forEach((l, idx) => {
            const x = Number(xValues[idx]) || 0
            const y = Number(yValues[idx]) || 0
            if (isBubbleChart) {
              const r = rValues[idx] ? Number(rValues[idx]) || 10 : 10
              rawBubbleData.push({ label: l, x, y, r: Math.abs(r) })
            } else {
              processedData.labels.push(l)
              processedData.values.push({ x, y })
            }
          })
        } else {
          const x = Number(xValue) || 0
          const y = Number(yValue) || 0

          if (isBubbleChart) {
            const r = Number(rValue) || 10
            rawBubbleData.push({ label, x, y, r: Math.abs(r) })
          } else {
            processedData.labels.push(label)
            processedData.values.push({ x, y })
          }
        }
      } else {
        let value = point.value

        if (typeof value === 'string') {
          value = renderTemplate(value, context)
        }

        if (typeof label === 'string' && label.includes(',') && typeof value === 'string' && value.includes(',')) {
          const labels = label.split(',')
          const values = value.split(',')

          labels.forEach((l, idx) => {
            const v = values[idx]
            if (v !== undefined) {
              processedData.labels.push(l)
              processedData.values.push(Number(v) || 0)
            }
          })
        } else {
          processedData.labels.push(label)
          processedData.values.push(Number(value) || 0)
        }
      }
    } catch (err) {
      console.warn('Error processing chart data point:', err)
      if (isBubbleChart) {
        rawBubbleData.push({ label, x: 0, y: 0, r: 10 })
      } else if (isXYChart) {
        processedData.labels.push(label)
        processedData.values.push({ x: 0, y: 0 })
      } else {
        processedData.labels.push(label)
        processedData.values.push(0)
      }
    }
  })

  // For bubble charts, normalize the R values and add to processed data
  if (isBubbleChart && rawBubbleData.length > 0) {
    const bubbleValues = rawBubbleData.map((d) => ({ x: d.x, y: d.y, r: d.r }))
    const normalizedValues = normalizeRValues(bubbleValues)

    rawBubbleData.forEach((d, idx) => {
      processedData.labels.push(d.label)
      processedData.values.push(normalizedValues[idx])
    })
  }

  return processedData
}

function processChartData(chartData: ExtendedChartConfiguration, context: any, overrideType?: string) {
  // Check if we have the new multi-series format
  if (chartData.series && Array.isArray(chartData.series) && chartData.series.length > 0) {
    // Multi-series format - process each series
    const datasets: any[] = []
    let allLabels: string[] = []
    const isSingleSeries = chartData.series.length === 1

    chartData.series.forEach((series, index) => {
      // Use overrideType if provided (from runtime type switcher), otherwise use series chart_type
      const seriesChartType = overrideType || series.chart_type || (chartData.type as string) || 'bar'
      const processedData = processSeriesDataPoints(series.data_points || [], seriesChartType, context)

      // Collect labels (use the longest label array)
      if (processedData.labels.length > allLabels.length) {
        allLabels = processedData.labels
      }

      // Get colors from pre-built datasets (set by settings component using color scheme)
      const preBuiltDataset = chartData.data?.datasets?.[index]
      let backgroundColor = preBuiltDataset?.background_color || preBuiltDataset?.backgroundColor
      let borderColor = preBuiltDataset?.border_color || preBuiltDataset?.borderColor

      // Handle palette references for single series (array of colors)
      if (isSingleSeries && Array.isArray(backgroundColor)) {
        // First, resolve palette references to actual colors
        const resolvedColors = backgroundColor.map((color: any, i: number) => {
          if (typeof color === 'string' && color.startsWith('palette')) {
            const paletteIndex = parseInt(color.replace('palette', ''))
            const validIndex = paletteIndex >= 0 ? paletteIndex % palette.value.length : i
            return palette.value[validIndex]
          }
          return color
        })
        // Then expand to match data points count (cycle through colors)
        const dataPointsCount = processedData.values.length
        backgroundColor = Array.from({ length: dataPointsCount }, (_, i) => resolvedColors[i % resolvedColors.length])
        borderColor = [...backgroundColor]
      } else if (typeof backgroundColor === 'string' && backgroundColor.startsWith('palette')) {
        // Handle palette reference for multi-series (single color)
        const paletteIndex = parseInt(backgroundColor.replace('palette', ''))
        const validIndex = paletteIndex >= 0 ? paletteIndex % palette.value.length : index
        backgroundColor = palette.value[validIndex]
        borderColor = backgroundColor
      }

      // Build dataset options
      const datasetOptions: any = {
        label: series.name,
        data: processedData.values,
        backgroundColor: backgroundColor || palette.value[index % palette.value.length],
        borderColor: borderColor || palette.value[index % palette.value.length],
        borderWidth: 1,
      }

      // For mixed charts, set the type per dataset
      if (!isSingleSeries) {
        if (series.chart_type === 'area') {
          datasetOptions.type = 'line'
          datasetOptions.fill = true
          datasetOptions.tension = 0.4
        } else {
          datasetOptions.type = series.chart_type
        }
      }

      // Handle special chart types
      if (series.chart_type === 'area') {
        datasetOptions.fill = true
        datasetOptions.tension = 0.4
      }

      // Add tension for line charts
      if (['line', 'area'].includes(series.chart_type)) {
        datasetOptions.tension = 0.4
      }

      datasets.push(datasetOptions)
    })

    return {
      ...chartData,
      type: chartData.type || chartData._primary_chart_type || 'bar',
      data: {
        ...chartData.data,
        labels: allLabels,
        datasets,
      },
    }
  }

  // Legacy single-series format (data_points)
  // Use overrideType if provided (from runtime type switcher), otherwise use chartData.type
  const chartType = overrideType || (chartData.type as string) || 'bar'
  const processedData = processSeriesDataPoints(chartData.data_points || [], chartType, context)

  // Process colors based on color scheme type
  let backgroundColor = chartData.data?.datasets?.[0]?.background_color || []
  let borderColor = chartData.data?.datasets?.[0]?.border_color || []

  if (chartData.color_scheme_type === 'theme' && Array.isArray(backgroundColor)) {
    if (
      backgroundColor?.length &&
      backgroundColor[0].startsWith('palette') &&
      palette.value.length > backgroundColor.length
    ) {
      const newBackgroundColor = []
      for (let i = 0; i < palette.value.length; i++) {
        if (i < backgroundColor.length) {
          const paletteIndex = parseInt(backgroundColor[i].replace('palette', ''))
          const validIndex = paletteIndex >= 0 ? paletteIndex % palette.value.length : i
          newBackgroundColor.push(palette.value[validIndex])
        } else {
          newBackgroundColor.push(palette.value[i])
        }
      }
      backgroundColor = newBackgroundColor
    } else {
      backgroundColor = backgroundColor.map((color: string, i) => {
        if (typeof color === 'string' && color.startsWith('palette')) {
          const paletteIndex = parseInt(color.replace('palette', ''))
          const validIndex = paletteIndex >= 0 ? paletteIndex % palette.value.length : i
          return palette.value[validIndex]
        }
        return color
      })
    }
    borderColor = [...backgroundColor]
  }

  return {
    ...chartData,
    type: chartData.type || 'bar',
    data: {
      ...chartData.data,
      labels: processedData.labels,
      datasets: [
        {
          ...chartData.data?.datasets?.[0],
          data: processedData.values,
          backgroundColor,
          borderColor,
          borderWidth: 1,
        },
      ],
    },
  }
}

function initChart(overrideType?: string) {
  if (!props.data || !chartRef.value || !isChartLoaded.value) {
    return
  }

  if (chart) {
    chart.destroy()
  }

  // Use provided context if available, otherwise fall back to canvas context
  const context =
    props.data.context ??
    (!isEmpty(activeCanvas.value?.context) || isCanvas.value
      ? { ...crmShape.value, ...activeCanvas.value?.context }
      : crmShape.value)

  const processedChartData = processChartData(props.data, context, overrideType)

  // Determine the chart type to use
  let chartType = overrideType || currentChartType.value || processedChartData.type || 'bar'

  // Handle "area" chart type - it's actually a line chart with fill: true
  const isAreaChart = chartType === 'area'
  if (isAreaChart) {
    chartType = 'line'
  }

  // Convert snake_case to camelCase for Chart.js
  if (processedChartData.data?.datasets) {
    processedChartData.data.datasets = processedChartData.data.datasets.map((dataset) => {
      const { background_color, border_color, point_style, fill, tension, show_line, ...rest } = dataset
      return {
        ...rest,
        backgroundColor: dataset.backgroundColor || background_color || ['#000000'],
        borderColor: dataset.borderColor || border_color || ['#000000'],
        pointStyle: point_style as any,
        fill: isAreaChart ? true : fill,
        tension: isAreaChart ? (tension ?? 0.4) : tension,
        showLine: show_line,
      }
    })
  }

  // Convert snake_case scales options to camelCase
  let scales = processedChartData.options?.scales
  if (scales) {
    const convertedScales: any = {}
    if (scales.x) {
      convertedScales.x = { ...scales.x }
    }
    if (scales.y) {
      convertedScales.y = {
        ...scales.y,
        beginAtZero: (scales.y as any).begin_at_zero,
      }
      delete convertedScales.y.begin_at_zero
    }
    if ((scales as any).r) {
      convertedScales.r = {
        ...(scales as any).r,
        beginAtZero: (scales as any).r.begin_at_zero,
        ticks: (scales as any).r.ticks
          ? {
              stepSize: (scales as any).r.ticks.step_size,
            }
          : undefined,
      }
    }
    scales = convertedScales
  }

  // Remove scales for pie/doughnut/polar charts when switching types
  if (['pie', 'doughnut', 'polarArea'].includes(chartType)) {
    scales = undefined
  }

  // Build tooltip callback for percentage display and bubble chart original R values
  const tooltipCallbacks: any = {}
  const isBubble = chartType === 'bubble'

  // Always set up tooltip callback for bubble charts to show original R value
  if (props.data.percent_display || isBubble) {
    tooltipCallbacks.label = function (context: any) {
      const label = context.dataset.label || ''
      const value = context.raw

      // For bubble charts, show x, y, and original R value
      if (isBubble && typeof value === 'object' && value !== null) {
        const x = value.x
        const y = value.y
        // Use originalR if available, otherwise fall back to r
        const r = value.originalR !== undefined ? value.originalR : value.r
        return `${label}${label ? ': ' : ''}(${x}, ${y}, ${r})`
      }

      // If using percentage display and value is a number between 0 and 1
      if (props.data?.percent_display && typeof value === 'number' && value >= 0 && value <= 1) {
        return `${label}${label ? ': ' : ''}${(value * 100).toFixed(2)}%`
      }

      return `${label}${label ? ': ' : ''}${value}`
    }
  }

  // Ensure we have a valid chart type before creating the chart
  const chartConfig = {
    ...processedChartData,
    type: chartType,
    options: merge({}, processedChartData.options, {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: processedChartData.options?.index_axis,
      scales,
      plugins: {
        ...processedChartData.options?.plugins,
        tooltip:
          props.data.percent_display || isBubble
            ? {
                callbacks: tooltipCallbacks,
              }
            : undefined,
      },
    }),
  }

  chart = new window.Chart(chartRef.value, chartConfig as ChartConfiguration)
}

// Watch for data changes with immediate and deep options
watch(
  () => [props.data, activeCanvas.value?.context, isChartLoaded.value, palette.value],
  () => {
    if (isChartLoaded.value && window.Chart) {
      initChart()
    }
  },
  { immediate: true, deep: true },
)

onMounted(async () => {
  await ensureChartJsLoaded()
  await nextTick()
  isChartLoaded.value = true
})
</script>

<style lang="scss" scoped>
.cb-data-charts-raw {
  width: 100%;
  height: 100%;
  min-height: 300px;
  max-height: 750px; // animation doesn't work with big height
  position: relative;

  &.is-fullscreen {
    position: fixed;
    top: 2%;
    left: 2%;
    width: 96%;
    height: 96%;
    max-height: none;
    z-index: 9999;
    background-color: white;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    padding: 16px;
  }

  &__controls {
    position: absolute;
    top: 16px;
    right: 60px;
    z-index: 10;
    display: flex;
    gap: 4px;
  }
}
</style>
